import { Link } from "react-router-dom";
import TrustBadges from "./TrustBadges";
import type { LandingPageContent } from "@shared/landingPages";

/**
 * HowItWorks — "How We Make Compliance Effortless" 3-step section.
 * Renders below the quiz on the DSCSA landing page.
 * Converts interest into confidence before the final CTA.
 *
 * onCtaClick — fired when the bottom "Create Your Free…" button is clicked.
 */
export default function HowItWorks({ onCtaClick, content }: { onCtaClick?: () => void; content: LandingPageContent }) {
  return (
    <section className="bg-background py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section heading */}
        <div className="text-center mb-12 lg:mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest text-accent">Simple steps, no surprises</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary tracking-tight mt-2">How it works</h2>
        </div>

        {/* Connected step timeline.
            Managed content allows 3–5 steps, so the desktop column count and
            the connecting-line insets are derived from the step count. The
            single-row horizontal layout only kicks in at lg; below that every
            count uses the vertical connector layout, so the line can never
            span a wrapped row. The line lives OUTSIDE the <ol> (sibling in a
            relative wrapper) so the list contains only <li> children. */}
        {(() => {
          const steps = content.howItWorks;
          const count = steps.length;
          const gridCols =
            count <= 3 ? "lg:grid-cols-3" : count === 4 ? "lg:grid-cols-4" : "lg:grid-cols-5";
          // Line runs from the center of the first column to the center of the last.
          const lineInset = `${(100 / (2 * count)).toFixed(2)}%`;
          return (
            <div className="relative">
              <div
                className="hidden lg:block absolute top-6 h-0.5 bg-gray-200"
                style={{ left: lineInset, right: lineInset }}
                aria-hidden="true"
              />
              <ol className={`grid grid-cols-1 ${gridCols} gap-10 lg:gap-8`}>
                {steps.map((step, index) => (
                  <li key={step.title} className="relative flex lg:flex-col items-start lg:items-center gap-5 lg:gap-0 lg:text-center">
                    {/* Vertical connector (stacked layout) */}
                    {index < count - 1 && (
                      <div className="lg:hidden absolute left-6 top-14 bottom-[-2.5rem] w-0.5 bg-gray-200" aria-hidden="true" />
                    )}
                    {/* Step number circle */}
                    <span className="relative z-10 w-12 h-12 rounded-full bg-accent text-white font-extrabold text-lg flex items-center justify-center flex-shrink-0 shadow-md shadow-accent/20 lg:mb-5">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="text-lg font-bold text-primary">{step.title}</h3>
                      <p className="text-gray-600 leading-relaxed text-sm mt-2 lg:max-w-xs lg:mx-auto">{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          );
        })()}

        {/* Second CTA block */}
        <div className="mt-12 lg:mt-16 flex flex-col items-center gap-4">
          <Link
              to={content.ctaDestination}
            onClick={onCtaClick}
            className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-white font-bold text-base px-8 py-4 rounded-btn shadow-lg shadow-emerald-900/20 transition-colors duration-200"
          >
            {content.ctaText}
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>

          <TrustBadges variant="light" signals={content.trustSignals} />
        </div>

      </div>
    </section>
  );
}
